import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookMarked,
  ChevronDown,
  ChevronRight,
  Database,
  ExternalLink,
  FileText,
  Globe,
  Loader2,
  RefreshCw,
  Workflow,
  Wrench,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  CollectionDocument,
  getCollectionDocuments,
} from '@/services/data-sources/get-collection-documents';
import { useOrgNavigate } from '@/hooks/use-org-navigate';

const SOURCE_TYPE_META: Record<string, { label: string; Icon: typeof Globe }> =
  {
    web: { label: 'Web', Icon: Globe },
    deep_research: { label: 'Deep Research', Icon: BookMarked },
    collection: { label: 'Collection', Icon: Database },
    upload: { label: 'Upload', Icon: FileText },
    tool: { label: 'Tool', Icon: Wrench },
  };

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const normalizeLabels = (labels?: string[] | string): string[] => {
  if (Array.isArray(labels)) return labels;
  if (typeof labels === 'string') {
    return labels
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean);
  }
  return [];
};

const DocumentRow = ({
  document,
  showProvenance,
}: {
  document: CollectionDocument;
  showProvenance: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useOrgNavigate();
  const { payload } = document;
  const title =
    payload.title ||
    payload.original_filename ||
    document.external_id ||
    'Untitled';
  const typeMeta = payload.source_type
    ? SOURCE_TYPE_META[payload.source_type]
    : undefined;
  const capturedAt = payload.captured_at || payload.datetime;

  return (
    <div className='rounded-md border'>
      <button
        type='button'
        onClick={() => setExpanded((v) => !v)}
        className='flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40'
      >
        {expanded ? (
          <ChevronDown className='size-4 shrink-0 text-muted-foreground' />
        ) : (
          <ChevronRight className='size-4 shrink-0 text-muted-foreground' />
        )}
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='truncate font-medium'>{title}</span>
            {typeMeta && (
              <Badge variant='outline' className='gap-1 text-[10px]'>
                <typeMeta.Icon className='size-3' />
                {typeMeta.label}
              </Badge>
            )}
            {normalizeLabels(payload.labels).map((label) => (
              <Badge key={label} variant='secondary' className='text-[10px]'>
                {label}
              </Badge>
            ))}
          </div>
          {payload.url && (
            <div className='mt-0.5 truncate text-xs text-muted-foreground'>
              {payload.url}
            </div>
          )}
        </div>
        <div className='shrink-0 text-right text-xs text-muted-foreground'>
          <div>{formatDate(capturedAt)}</div>
          <div>
            {document.chunk_count} chunk{document.chunk_count === 1 ? '' : 's'}
          </div>
        </div>
      </button>

      {expanded && (
        <div className='space-y-3 border-t px-4 py-3 text-sm'>
          {payload.url && (
            <a
              href={payload.url}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center gap-1 text-primary hover:underline break-all'
            >
              {payload.url}
              <ExternalLink className='size-3 shrink-0' />
            </a>
          )}
          {document.text_preview && (
            <blockquote className='whitespace-pre-wrap border-l-2 border-muted-foreground/30 pl-3 text-xs text-muted-foreground'>
              {document.text_preview}
            </blockquote>
          )}

          {showProvenance && (
            <div className='grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2'>
              {payload.query && (
                <div>
                  <span className='text-muted-foreground'>
                    Original query:{' '}
                  </span>
                  {payload.query}
                </div>
              )}
              {payload.note && (
                <div>
                  <span className='text-muted-foreground'>Saved because: </span>
                  {payload.note}
                </div>
              )}
              {payload.workflow_id && (
                <div className='flex items-center gap-1'>
                  <span className='text-muted-foreground'>Workflow: </span>
                  <button
                    type='button'
                    className='inline-flex items-center gap-1 text-primary hover:underline'
                    onClick={() => navigate(`/workflow/${payload.workflow_id}`)}
                  >
                    <Workflow className='size-3' />
                    {String(payload.workflow_id).slice(0, 8)}…
                  </button>
                </div>
              )}
              {payload.run_id && (
                <div>
                  <span className='text-muted-foreground'>Run: </span>
                  <span className='font-mono'>{payload.run_id}</span>
                </div>
              )}
              {payload.session_id && (
                <div>
                  <span className='text-muted-foreground'>Session: </span>
                  <span className='font-mono'>{payload.session_id}</span>
                </div>
              )}
              {payload.node_id && (
                <div>
                  <span className='text-muted-foreground'>Saved by node: </span>
                  <span className='font-mono'>{payload.node_id}</span>
                </div>
              )}
            </div>
          )}

          <details className='text-xs'>
            <summary className='cursor-pointer text-muted-foreground'>
              Full record payload
            </summary>
            <pre className='mt-2 max-h-72 overflow-auto rounded bg-muted/50 p-2'>
              {JSON.stringify(payload, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

interface CollectionDocumentsListProps {
  collectionId: string;
  /** 'sources' shows citation provenance prominently; 'records' is generic. */
  variant?: 'records' | 'sources';
}

// Logical qdrant documents -- captured citations, WordPress and API ingests --
// which are invisible in the Files (S3 uploads) tab.
export const CollectionDocumentsList = ({
  collectionId,
  variant = 'records',
}: CollectionDocumentsListProps) => {
  const [documents, setDocuments] = useState<CollectionDocument[]>([]);
  const [nextOffset, setNextOffset] = useState<string | number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const load = useCallback(
    async (offset: string | number | null = null) => {
      setLoading(true);
      setError('');
      try {
        const response = await getCollectionDocuments(collectionId, {
          offset,
        });
        setDocuments((prev) =>
          offset == null
            ? response.documents
            : [...prev, ...response.documents],
        );
        setNextOffset(response.next_offset ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load records');
      } finally {
        setLoading(false);
      }
    },
    [collectionId],
  );

  useEffect(() => {
    setDocuments([]);
    setNextOffset(null);
    load(null);
  }, [load]);

  const availableTypes = useMemo(
    () =>
      Array.from(
        new Set(
          documents
            .map((d) => d.payload.source_type)
            .filter((t): t is string => Boolean(t)),
        ),
      ),
    [documents],
  );

  const visibleDocuments = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return documents.filter((doc) => {
      if (typeFilter && doc.payload.source_type !== typeFilter) return false;
      if (!needle) return true;
      const haystack = [
        doc.payload.title,
        doc.payload.url,
        doc.payload.original_filename,
        doc.payload.query,
        doc.payload.note,
        ...normalizeLabels(doc.payload.labels),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [documents, filter, typeFilter]);

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center gap-2'>
        <div className='w-96 max-w-full'>
          <SearchInput
            value={filter}
            onChange={setFilter}
            placeholder='Filter by title, URL, label, query…'
            aria-label='Filter records'
          />
        </div>
        {availableTypes.map((type) => (
          <Badge
            key={type}
            variant={typeFilter === type ? 'default' : 'outline'}
            className='cursor-pointer'
            onClick={() => setTypeFilter(typeFilter === type ? null : type)}
          >
            {SOURCE_TYPE_META[type]?.label ?? type}
          </Badge>
        ))}
        <Button
          variant='ghost'
          size='sm'
          className='ml-auto'
          onClick={() => load(null)}
          disabled={loading}
        >
          <RefreshCw className='size-4' />
          Refresh
        </Button>
      </div>

      {error && (
        <p className='text-sm text-rose-600 dark:text-rose-400'>{error}</p>
      )}

      {!loading && !error && documents.length === 0 && (
        <div className='rounded-lg border border-dashed px-6 py-10 text-center text-sm text-muted-foreground'>
          {variant === 'sources'
            ? 'No sources captured yet. Connect the Citation Store tool to an agent in a workflow and run it to start building your library.'
            : 'No records in this collection yet.'}
        </div>
      )}

      <div className='space-y-2'>
        {visibleDocuments.map((doc) => (
          <DocumentRow
            key={
              doc.external_id ?? `${doc.payload.index_time}-${doc.chunk_count}`
            }
            document={doc}
            showProvenance={variant === 'sources'}
          />
        ))}
      </div>

      <div className='flex items-center justify-between text-xs text-muted-foreground'>
        <span>
          Showing {visibleDocuments.length} of {documents.length} loaded record
          {documents.length === 1 ? '' : 's'}
        </span>
        <div className='flex items-center gap-2'>
          {loading && <Loader2 className='size-4 animate-spin' />}
          {nextOffset != null && !loading && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => load(nextOffset)}
            >
              Load more
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
